"use client";

import { useState } from "react";
import YouTube from "react-youtube";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface PublicYoutubePreviewProps {
  videoUrl: string;
}

export default function PublicYoutubePreview({ videoUrl }: PublicYoutubePreviewProps) {
  const [isError, setIsError] = useState<boolean>(false);
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(true);
  const { t } = useTranslation(["profile"]);

  const { videoId, isShort } = getVideoInfo(videoUrl);

  const renderVideoContent = () => (
    <div className={`relative w-full ${isShort ? "pb-[70%] xl:pb-[180.78%]" : "pb-[56.25%]"}`}>
      <YouTube
        className={`absolute top-0 left-0 h-full w-full`}
        iframeClassName="w-full h-full"
        videoId={videoId}
        onError={() => setIsError(true)}
      />
    </div>
  );

  const renderVideoOrSkeleton = () => {
    if (!videoUrl || !videoId || isError) {
      return null;
    }

    return (
      <div className="relative w-full">
        <div className="hidden xl:block">{renderVideoContent()}</div>
        <Collapsible
          className="w-full xl:hidden"
          open={isCollapsibleOpen}
          onOpenChange={setIsCollapsibleOpen}
        >
          <CollapsibleTrigger asChild>
            <Button className="flex w-full items-center justify-between" variant="outline">
              <span>{t("profile:aboutSection.youtube")}</span>
              {isCollapsibleOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>{renderVideoContent()}</CollapsibleContent>
        </Collapsible>
      </div>
    );
  };

  return (
    <div className="bg-secondary/10 mx-auto w-full rounded-md p-2">{renderVideoOrSkeleton()}</div>
  );
}

function getVideoInfo(videoUrl: string | undefined) {
  if (!videoUrl) return { videoId: null, isShort: false };

  const videoIdMatch = videoUrl.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?/,
  );
  const videoId = videoIdMatch ? videoIdMatch[1] : null;
  const isShort = videoUrl.includes("/shorts/");

  return { videoId, isShort };
}
